import { yupResolver } from '@hookform/resolvers/yup';
import { Button as ButtonMUI, Grid, Tooltip, Typography } from '@mui/material';
import { IClientDeleteInputModel, IClientInputModel } from 'inputModels';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IClientViewModel } from 'viewModels';
import * as yup from 'yup';
import { useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';
import { ReactComponent as DeleteIcon } from '../../assets/icons/IconTrashCan.svg';
import { Button, Input, NavHeader } from '../../components';
import { ModalDeleteClient } from '../../components/Modals/ModalDeleteClient';
import { MenuOptionLeftCompany } from '../../components/MenuOptionLeft/Company';
import { useClients } from '../../context/clients';
import { CompanyController } from '../../controller/CompanyController';
import { Body, Container, Content } from './styles';
import { useApp, useAuth } from '../../context';
import { useAlert } from '../../hooks/useAlert';
import JoyrideTour from '../../components/JoyrideTour';
import TitlePage from '../../components/TitlePage';
import { TutorialType } from '../../types/enums';

export function Clients() {
  const { user } = useAuth();
  const app = useApp();
  const alert = useAlert();
  const { colors } = useTheme();
  const { clients, setClients, isLoadingClients, setEnableClientsLoad } = useClients();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [clientSelected, setClientSelected] = useState<IClientViewModel>({} as IClientViewModel);
  const clientToTutorial: IClientViewModel[] = [
    {
      id: uuid(),
      name: 'Cliente Tutorial',
    },
  ];
  const [defaultValues] = useState<IClientInputModel>({} as IClientInputModel);
  const [runningTutorial, setRunningTutorial] = useState(false);

  const steps = [
    {
      target: '#start-item',
      content:
        'Os clientes são os usuários dos serviços da sua empresa, a partir desse menu é possível adiconar novos clientes, editar ou visualizar os clientes já cadastrados.',
      disableBeacon: true,
    },
    {
      target: '#add-new-client',
      content:
        'Para cadastrar um novo cliente basta preencher o campo de nome e clicar em adicionar.',
    },
    {
      target: '#delete-client',
      content:
        'Para excluir um cliente basta clicar na lixeira e confirmar clicando no botão excluir.',
    },
  ];

  const clientSchema: yup.SchemaOf<IClientInputModel> = yup.object().shape({
    name: yup.string().required('O campo nome é obrigatório'),
  });

  const handleRemoveClient = (clientDeleted: IClientDeleteInputModel) => {
    CompanyController.deleteClient(clientDeleted).then(() => {
      setClients((prev) => prev?.filter((client) => client.id !== clientDeleted.id));
      setIsOpenDeleteModal(false);
    });
  };

  const {
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    control,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
    resolver: yupResolver(clientSchema),
  });

  const addClient = async (data: IClientInputModel) => {
    app.setLoading(true);
    await CompanyController.addClient(data)
      .then((id) => {
        setRunningTutorial(false);
        const newClient: IClientViewModel = {
          id,
          name: data.name,
        };
        setClients((prev) => [...prev, newClient]);
        reset({ name: '' });
      })
      .then(() => {
        alert.show({ message: 'Cliente adicionado com sucesso!', type: 'success' });
      })
      .catch((error: any) => {
        if (error?.response?.data?.Message === 'ALREADY_REGISTERED') {
          setError('name', { message: 'Já existe um cliente com esse nome!' });
          return;
        }
        alert.show({ message: 'Erro ao adicionar cliente!', type: 'error' });
      })
      .finally(() => {
        app.setLoading(false);
      });
  };

  useEffect(() => {
    app.setLoading(isLoadingClients);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingClients]);

  useEffect(() => {
    setEnableClientsLoad(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.CLIENT)) {
      setRunningTutorial(true);
    }
  }, [user.tutorialProgress]);
  return (
    <>
      <JoyrideTour
        steps={steps}
        type={TutorialType.CLIENT}
        run={runningTutorial}
        onCallbackFinished={() => setRunningTutorial(false)}
      />
      <NavHeader typeHeader="company" />
      <Body>
        <MenuOptionLeftCompany />
        <Container>
          <TitlePage
            id="start-item"
            title="Clientes"
            onRequestOpen={() => {
              setRunningTutorial(true);
            }}
          />
          <Content>
            <Input
              name="name"
              placeholder="Nome"
              className="clientName"
              control={control}
              variant="standard"
              errorText={errors.name?.message}
              lengthInput={100}
            />
            <div className="add">
              <Button
                id="add-new-client"
                text="Adicionar"
                dimensions={{ width: '13rem', height: '2.25rem' }}
                onClick={handleSubmit(addClient)}
              />
            </div>
          </Content>
          {clients.length || runningTutorial ? (
            <>
              <h2>Cadastrados</h2>
              <div className="branchesList">
                {runningTutorial &&
                  clientToTutorial.map((clientTutorial) => (
                    <Tooltip
                      disableInteractive
                      key={clientTutorial.id}
                      title={clientTutorial.name}
                      placement="top-start"
                      arrow>
                      <div className="branch" key={clientTutorial.id}>
                        <div className="headerBranch">
                          <h1>{clientTutorial.name}</h1>
                          <div>
                            <ButtonMUI
                              type="button"
                              className="delete"
                              id="delete-client"
                              disabled
                              onClick={() => {
                                setClientSelected(clientTutorial);
                                setIsOpenDeleteModal(true);
                              }}>
                              <DeleteIcon />
                            </ButtonMUI>
                          </div>
                        </div>
                      </div>
                    </Tooltip>
                  ))}
                {!runningTutorial &&
                  clients
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((client) => (
                      <Tooltip
                        disableInteractive
                        title={client.name}
                        placement="top-start"
                        arrow
                        key={client.id}>
                        <div className="branch">
                          <div className="headerBranch">
                            <h1>{client.name}</h1>
                            <div>
                              <ButtonMUI
                                type="button"
                                className="delete"
                                onClick={() => {
                                  setClientSelected(client);
                                  setIsOpenDeleteModal(true);
                                }}>
                                <DeleteIcon />
                              </ButtonMUI>
                            </div>
                          </div>
                        </div>
                      </Tooltip>
                    ))}
              </div>
            </>
          ) : (
            <Grid container padding="2rem" justifyContent="center">
              <Typography justifyContent="center" align="center" color={colors.primary}>
                Nenhum cliente encontrado...
              </Typography>
            </Grid>
          )}
        </Container>
        {clientSelected && isOpenDeleteModal && (
          <ModalDeleteClient
            isOpen={isOpenDeleteModal}
            onRequestClose={() => setIsOpenDeleteModal(false)}
            currentClientSelected={clientSelected}
            onRequestRemoveClient={(clientDeleted) => {
              handleRemoveClient(clientDeleted);
            }}
          />
        )}
      </Body>
    </>
  );
}
